import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  UserIcon, 
  UsersIcon,
  ArrowRightEndOnRectangleIcon,
  BuildingStorefrontIcon,
  PaperAirplaneIcon,
  MapIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import { getImageUrl } from '../config/runtime';
import {
  UserIcon as UserIconSolid, 
  UsersIcon as UsersIconSolid,
  PaperAirplaneIcon as PaperAirplaneIconSolid,
  MapIcon as MapIconSolid,
  HomeIcon as HomeIconSolid,
} from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';
import { useBottomNav } from '../contexts/BottomNavContext';
import { useQuery } from '@tanstack/react-query';
import { projectAPI, partnerAPI, createQueryKey } from '../services/api';
import { useProjectStore } from '../stores/projectStore';
import { useMobile, useKeyboard } from '../hooks/useMobile';
import { HeaderSlotContext, HEADER_SEARCH_SLOT_ID } from '../contexts/HeaderSlotContext';
import { useTokenGate } from '../hooks/useTokenGate';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { currentProject } = useProjectStore();
  const { isBottomNavVisible } = useBottomNav();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isDesktopSearchVisible, setDesktopSearchVisible] = useState(false);

  const headerSlotValue = useMemo(
    () => ({ setDesktopSearchVisible }),
    [setDesktopSearchVisible]
  );
  
  // Mobile hooks
  const { isMobile, viewportHeight } = useMobile();
  useTokenGate();
  const { isKeyboardOpen } = useKeyboard();

  // Scan-to-Play guests get reduced chrome on venue surfaces (their session,
  // their project, the partner page): no consumer navigation, no user menu —
  // one soft door out. Anywhere else (e.g. /new, /starters, /discover) a
  // guest is treated like a logged-out visitor: public nav + Sign In, so
  // wandering out of the venue world never strands them in stripped UI.
  const isGuest = user?.authProvider === 'guest';
  const isVenueSurface = /^\/(play\/|p\/|projects)/.test(location.pathname);
  const guestFocus = isGuest && isVenueSurface;
  const guestHomeLink = user?.app_settings?.guest_partner_slug
    ? `/p/${user.app_settings.guest_partner_slug}`
    : '/welcome';

  // Active project indicator (fallback to API if store not set)
  const { data: activeProjectResponse } = useQuery({
    queryKey: createQueryKey.activeProject(),
    queryFn: () => projectAPI.getActive(),
    enabled: !!isAuthenticated,
    staleTime: 5000,
    retry: false,
  });
  const activeProject = activeProjectResponse?.data?.project || activeProjectResponse?.data;

  // Venue owners (partners.ownerUserId) get a "My venue" entry in the app
  // chrome — the way back to /p/{slug}/dashboard from anywhere in regular
  // Okio. /partners/mine is 404-free: partner null = not a venue owner.
  const { data: myVenueResponse } = useQuery({
    queryKey: ['partner-mine'],
    queryFn: () => partnerAPI.getMine(),
    enabled: !!isAuthenticated && !isGuest,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const myVenue = myVenueResponse?.data?.data?.partner || null;
  // Note: 'paused' status is deprecated - Events should finish, Sites use session archiving
  // Keeping this check for backward compatibility with legacy projects
  const hasActiveProject = Boolean(
    (currentProject && (currentProject.status === 'ongoing' || currentProject.status === 'paused')) ||
    (activeProject && (activeProject.status === 'ongoing' || activeProject.status === 'paused'))
  );


  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
    navigate('/login', { replace: true });
  };


  // Helper function to determine if bottom nav should be hidden
  const shouldHideBottomNav = () => {
    const path = location.pathname;
    
    // Hide on projects page (both listing and individual project pages)
    // if (path === '/projects' || path.startsWith('/projects/')) {
    //   return true;
    // }

    // Hide on chat pages: /projects/{projectId}/chats/{chatId}
    // Match pattern: /projects/ followed by UUID, then /chats/, then UUID
    const chatPagePattern = /^\/projects\/[^/]+\/chats\/[^/]+$/;
    if (chatPagePattern.test(path)) {
      return true;
    }
    
    // Hide on starters detail pages (but not main starters page)
    if (path.startsWith('/starters/') && path !== '/starters/') {
      return true;
    }
    
    return false;
  };

  const getDisplayName = (user) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user?.firstName) {
      return user.firstName;
    } else if (user?.username) {
      return user.username;
    }
    return 'User';
  };

  const getInitials = (user) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    } else if (user?.firstName) {
      return user.firstName[0].toUpperCase();
    } else if (user?.username) {
      return user.username[0].toUpperCase();
    }
    return 'U';
  };

  // Determine smart home link destination for logged in users
  const getSmartHomeLink = () => {
    if (!isAuthenticated) {
      return '/'; // Default home for non-authenticated users
    }
    
    // Always go to home page for logged-in users
    return '/new';
  };

  // Some mobile routes render their own edge-to-edge headers/hero chrome and already
  // account for the status bar. Keeping Layout.safe-top on those routes creates a
  // duplicate inset strip above the page content.
  const pageManagesOwnTopSafeArea = () => {
    if (!isMobile) return false;

    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);

    if (path === '/new') return true;
    if (path === '/discover' || path === '/discover/') return true;
    if (segments[0] === 'discover' && segments.length === 2) return true;
    if (path === '/starters' || path === '/starters/') return true;
    if (segments[0] === 'starters' && segments.length === 2) return true;
    if (path === '/projects' || path === '/projects/') return true;
    if (segments[0] === 'projects' && segments.length === 2) return true;
    if (segments[0] === 'projects' && segments.length === 4 && segments[2] === 'chats') return true;

    return false;
  };

  const isPublicBrowseSurface = () => {
    if (isAuthenticated) return false;

    const path = location.pathname;
    return (
      path === '/starters' ||
      path === '/starters/' ||
      path.startsWith('/discover')
    );
  };

  const shouldShowMobileBottomNav =
    !shouldHideBottomNav() && !guestFocus && (isAuthenticated || isPublicBrowseSurface());

  return (
    <HeaderSlotContext.Provider value={headerSlotValue}>
    <div 
      className={`h-screen flex flex-col bg-white dark:bg-gray-900 ${pageManagesOwnTopSafeArea() ? '' : 'safe-top'} safe-left safe-right`}
      style={{
        // On Android WebView the viewport already shrinks when the keyboard opens.
        // Using the live viewport height avoids subtracting the keyboard twice.
        height: isMobile ? `${viewportHeight}px` : '100vh',
        paddingBottom: isKeyboardOpen ? 0 : 'var(--app-safe-area-bottom)',
      }}
    >
      {/* Header - Hidden on mobile and for non-authenticated users on homepage */}
      {!(isAuthenticated === false && location.pathname === '/') && (
        <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 text-black dark:text-white sticky top-0 z-50 flex-shrink-0 hidden md:block">
        <div className="grid grid-cols-[auto,1fr,auto] items-center h-14 mx-auto px-5 w-full gap-6">
          {/* Left side: Logo + Nav */}
          <div className="flex items-center gap-8">
            <div className="flex items-center space-x-2 text-sm pr-6">
              <Link
                to={guestFocus ? guestHomeLink : isGuest ? '/' : getSmartHomeLink()}
                className="flex items-center"
              >
                <img 
                  src="/logo_black.webp" 
                  alt="OKIO" 
                  className="h-6 w-auto dark:hidden"
                />
                <img 
                  src="/logo_white.webp" 
                  alt="OKIO" 
                  className="h-6 w-auto hidden dark:block"
                />
              </Link>
            </div>

            {!guestFocus && (
            <nav className="flex items-center space-x-6">
              {isAuthenticated && !isGuest && (
                <button
                  onClick={() => {
                    navigate('/projects', {
                      state: {
                        previousPath: location.pathname,
                        transitionDirection: 'forward',
                      },
                    });
                  }}
                  className={`relative inline-flex items-center text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/projects')
                      ? 'text-emerald-600 dark:text-emerald-400 font-semibold'
                      : 'text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400'
                  }`}
                >
                  <span>Projects</span>
                  {hasActiveProject && (
                    <span className="absolute -top-1 -right-2 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </button>
              )}
              <button
                onClick={() => {
                  navigate('/starters', {
                    state: {
                      previousPath: location.pathname,
                      transitionDirection: 'forward',
                    },
                  });
                }}
                className={`text-sm font-medium transition-colors ${
                  location.pathname.startsWith('/starters')
                    ? 'text-emerald-600 dark:text-emerald-400 font-semibold'
                    : 'text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400'
                }`}
              >
                Starters
              </button>
              <Link
                to="/discover"
                className={`text-sm font-medium transition-colors ${
                  location.pathname.startsWith('/discover')
                    ? 'text-emerald-600 dark:text-emerald-400 font-semibold'
                    : 'text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400'
                }`}
              >
                Discover
              </Link>
              {isAuthenticated && !isGuest && (
                <>
                  <Link
                    to="/profile"
                    className={`text-sm font-medium transition-colors ${
                      location.pathname.startsWith('/profile')
                        ? 'text-emerald-600 dark:text-emerald-400 font-semibold'
                        : 'text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400'
                    }`}
                  >
                    Profile
                  </Link>
                  {user?.isSuperuser && (
                  <button
                    onClick={() => {
                      navigate('/admin');
                    }}
                    className={`text-sm font-medium transition-colors ${
                      location.pathname.startsWith('/admin')
                        ? 'text-emerald-600 dark:text-emerald-400 font-semibold'
                        : 'text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400'
                    }`}
                  >
                    Admin
                  </button>
                  )}
                </>
              )}
            </nav>
            )}
          </div>

          {/* Center: Desktop Search Slot */}
          <div
            className={`hidden lg:flex justify-center transition-all duration-200 w-full ${
              isDesktopSearchVisible
                ? 'opacity-100 px-4'
                : 'opacity-0 pointer-events-none px-0'
            }`}
            aria-hidden={!isDesktopSearchVisible}
          >
            <div id={HEADER_SEARCH_SLOT_ID} className="w-full max-w-lg" />
          </div>

          {/* Right side: User Menu */}
          <div className="flex gap-3 items-center justify-end">

            {guestFocus ? (
              <Link
                to="/welcome"
                className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
              >
                Discover Okio →
              </Link>
            ) : isAuthenticated && !isGuest ? (
              <div className="relative flex items-center gap-3">
                {/* Venue owner's door to their dashboard (desktop; mobile
                    reaches it via the avatar menu below) */}
                {myVenue && (
                  <Link
                    to={`/p/${myVenue.slug}/dashboard`}
                    className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-emerald-300 dark:border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-sm font-medium transition-colors"
                  >
                    <BuildingStorefrontIcon className="w-4 h-4" />
                    My venue
                  </Link>
                )}
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center bg-gray-200 dark:bg-gray-600 border dark:border-gray-500 text-black dark:text-white rounded-full text-xs font-medium"
                >
                  <div className="w-8 h-8 bg-emerald-600 dark:bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold overflow-hidden">
                    {user?.avatar ? (
                      <img 
                        src={getImageUrl(user.avatar)} 
                        alt={getDisplayName(user)} 
                        className="w-8 h-8 rounded-full object-cover" 
                      />
                    ) : (
                      getInitials(user)
                    )}
                  </div>
                </button>
                
                {/* User Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 top-8 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{getDisplayName(user)}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
                    </div>
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <UserIcon className="w-4 h-4 mr-3" />
                      Profile
                    </Link>

                    {myVenue && (
                      <Link
                        to={`/p/${myVenue.slug}/dashboard`}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <BuildingStorefrontIcon className="w-4 h-4 mr-3" />
                        My venue dashboard
                      </Link>
                    )}

                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <ArrowRightEndOnRectangleIcon className="w-4 h-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-emerald-500 dark:bg-emerald-500 dark:hover:bg-emerald-400"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </header>
      )}

      {/* Click outside to close menu */}
      {showUserMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowUserMenu(false)}
        />
      )}

      {/* Main content */}
      <main 
        className={`flex-1 min-h-0 ${
          // Detail pages need their own scrolling (content flows naturally)
          (location.pathname.includes('/projects/') && !location.pathname.endsWith('/projects')) ||
          (location.pathname.startsWith('/starters/') && location.pathname !== '/starters') ||
          (location.pathname.startsWith('/discover/') && location.pathname !== '/discover' && location.pathname !== '/discover/') ||
          location.pathname.startsWith('/users/') ||
          location.pathname === '/profile' ||
          location.pathname === '/welcome' ||
          location.pathname === '/ops' ||
          location.pathname === '/new'
            ? 'overflow-auto mobile-scroll'
            // Main listing pages manage their own internal scroll containers
            : 'overflow-hidden'
        } ${
          shouldShowMobileBottomNav
            ? 'sm:mb-0' 
            : 'mb-0'
        }`}
        style={{
          paddingBottom: isKeyboardOpen && isMobile ? '1rem' : undefined,
        }}
      >
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      {shouldShowMobileBottomNav && (
        <nav 
          className={`md:hidden fixed left-0 right-0 bg-white dark:bg-gray-900 z-[50000] mobile-bottom-nav border-t dark:border-t-0 border-gray-200 dark:border-gray-700 transition-transform duration-300 ease-in-out ${
            isKeyboardOpen ? 'hidden' : isBottomNavVisible ? 'block translate-y-0' : 'block translate-y-full'
          }`}
          style={{
            bottom: isKeyboardOpen ? 'auto' : 0,
            paddingBottom: 'var(--app-safe-area-bottom-nav)',
          }}
        >
          <div className="flex justify-between items-center">
            {isAuthenticated && !isGuest ? (
              <>
                {/* Projects */}
                <button
                  onClick={() => {
                    navigate('/projects', {
                      state: {
                        previousPath: location.pathname,
                        transitionDirection: 'forward',
                      },
                    });
                  }}
                  className={`flex-1 flex flex-col items-center p-2 rounded transition-colors mobile-button no-tap-highlight ripple-effect ${
                    location.pathname.startsWith('/projects') 
                      ? 'text-black dark:text-white' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400'
                  }`}
                >
                  <div className="relative">
                    {location.pathname.startsWith('/projects') ? (
                      <PaperAirplaneIconSolid className="w-6 h-6" />
                    ) : (
                      <PaperAirplaneIcon className="w-6 h-6" />
                    )}
                    {hasActiveProject && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                    )}
                  </div>
                  <span className="text-xs mt-1 font-medium">Projects</span>
                </button>

                {/* Starters */}
                <button
                  onClick={() => {
                    navigate('/starters', {
                      state: {
                        previousPath: location.pathname,
                        transitionDirection: 'forward',
                      },
                    });
                  }}
                  className={`flex-1 flex flex-col items-center p-2 rounded transition-colors mobile-button no-tap-highlight ripple-effect ${
                    location.pathname.startsWith('/starters') 
                      ? 'text-black dark:text-white' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400'
                  }`}
                >
                  {location.pathname.startsWith('/starters') ? (
                    <MapIconSolid className="w-6 h-6" />
                  ) : (
                    <MapIcon className="w-6 h-6" />
                  )}
                  <span className="text-xs mt-1 font-medium">Starters</span>
                </button>

                {/* Discover */}
                <button
                  onClick={() => {
                    navigate('/discover', {
                      state: {
                        previousPath: location.pathname,
                        transitionDirection: 'forward',
                      },
                    });
                  }}
                  className={`flex-1 flex flex-col items-center p-2 rounded transition-colors mobile-button no-tap-highlight ripple-effect ${
                    location.pathname.startsWith('/discover') 
                      ? 'text-black dark:text-white' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400'
                  }`}
                >
                  {location.pathname.startsWith('/discover') ? (
                    <UsersIconSolid className="w-6 h-6" />
                  ) : (
                    <UsersIcon className="w-6 h-6" />
                  )}
                  <span className="text-xs mt-1 font-medium">Discover</span>
                </button>

                {/* Profile */}
                <Link
                  to="/profile"
                  className={`flex-1 flex flex-col items-center p-2 rounded transition-colors mobile-button no-tap-highlight ripple-effect ${
                    location.pathname === '/profile' 
                      ? 'text-black dark:text-white' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <div className="relative">
                    {user?.avatar ? (
                      <img 
                        src={getImageUrl(user.avatar)} 
                        alt={getDisplayName(user)} 
                        className="w-6 h-6 rounded-full object-cover" 
                      />
                    ) : (
                      location.pathname === '/profile' ? (
                        <UserIconSolid className="w-6 h-6" />
                      ) : (
                        <UserIcon className="w-6 h-6" />
                      )
                    )}
                  </div>
                  <span className="text-xs mt-1">Profile</span>
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/"
                  className={`flex-1 flex flex-col items-center p-2 rounded transition-colors mobile-button no-tap-highlight ripple-effect ${
                    location.pathname === '/'
                      ? 'text-black dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400'
                  }`}
                >
                  {location.pathname === '/' ? (
                    <HomeIconSolid className="w-6 h-6" />
                  ) : (
                    <HomeIcon className="w-6 h-6" />
                  )}
                  <span className="text-xs mt-1 font-medium">Home</span>
                </Link>

                <Link
                  to="/starters"
                  className={`flex-1 flex flex-col items-center p-2 rounded transition-colors mobile-button no-tap-highlight ripple-effect ${
                    location.pathname.startsWith('/starters')
                      ? 'text-black dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400'
                  }`}
                >
                  {location.pathname.startsWith('/starters') ? (
                    <MapIconSolid className="w-6 h-6" />
                  ) : (
                    <MapIcon className="w-6 h-6" />
                  )}
                  <span className="text-xs mt-1 font-medium">Starters</span>
                </Link>

                <Link
                  to="/discover"
                  className={`flex-1 flex flex-col items-center p-2 rounded transition-colors mobile-button no-tap-highlight ripple-effect ${
                    location.pathname.startsWith('/discover')
                      ? 'text-black dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400'
                  }`}
                >
                  {location.pathname.startsWith('/discover') ? (
                    <UsersIconSolid className="w-6 h-6" />
                  ) : (
                    <UsersIcon className="w-6 h-6" />
                  )}
                  <span className="text-xs mt-1 font-medium">Discover</span>
                </Link>

                <Link
                  to="/login"
                  className={`flex-1 flex flex-col items-center p-2 rounded transition-colors mobile-button no-tap-highlight ripple-effect ${
                    location.pathname === '/login'
                      ? 'text-black dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {location.pathname === '/login' ? (
                    <UserIconSolid className="w-6 h-6" />
                  ) : (
                    <UserIcon className="w-6 h-6" />
                  )}
                  <span className="text-xs mt-1 font-medium">Sign In</span>
                </Link>
              </>
            )}
          </div>
        </nav>
      )}
    </div>
    </HeaderSlotContext.Provider>
  );
};

export default Layout; 